import { useCallback, useEffect, useRef, useState } from "react";
import { useThree, useFrame } from "@react-three/fiber";
import { useGLSettings } from '../hooks/useGLSettings';
import { getEnvironmentProps } from '../utils';
import { debug as debugLog} from "../utils/logger";
import { Helpers } from './Helpers';
import { Model } from './Model';
import { DreiStage } from './DreiStage';
import { PostProcessing } from "./PostProcessing";
import { CustomEnvironment } from "./CustomEnvironment";
import { HDRIFlatBackground } from "./HDRIFlatBackground";
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { getCameraProps } from "../utils/getCameraProps";
import { DEG2RAD } from "three/src/math/MathUtils";
import { TextNode } from "./TextNode";

// Any negative number so this will happen before frame is rendered
const RENDER_PRIORITY_CAMERA_RESET = -100;

export const CalcOrbitControlsDistance = (vFovDeg) => {
  const tanHalfFov = Math.tan(0.5 * vFovDeg * DEG2RAD)
  const maxDistance = 8 / tanHalfFov;
  const minDistance = Math.min(Math.SQRT2, 0.25 / tanHalfFov);
  return { maxDistance, minDistance}
}

export const Stage = ({
                        scene,
                        camera,
                        lights,
                        general,
	                      shadows,
	                      model,
	                      debug,
	                      postProcessing,
	                      onModelReady,
                        onRenderingReady = null,
                        noRender = false,
	                      onModelLoadProgress = null,
	                      onModelClick = null,
	                      onModelPointerOver = null,
	                      onModelPointerOut = null,
	                      modelContent,
                        textNodes,
	                      children,
                      }) => {
  const refModel = useRef(null);
  const refCamera = useRef(null);

  useFrame((state) => {
    if (state.controls && state.camera?.userData) {
      const cameraTargetChanged = 
        (state.controls.target.x != state.camera.userData.cameraTargetPositionV3.x) ||
        (state.controls.target.y != state.camera.userData.cameraTargetPositionV3.y) ||
        (state.controls.target.z != state.camera.userData.cameraTargetPositionV3.z);
      if (cameraTargetChanged || state.camera.userData.configChanged) {
        // In order to reset position when config is updated without image pop
        if (state.camera.userData.configChanged) {
          state.camera.userData.configChanged = false;
          state.camera.fov = state.camera.userData.vFovDeg;
          state.camera.position.copy(state.camera.userData.cameraInitPositionV3);
        }
        state.camera.lookAt(state.camera.userData.cameraTargetPositionV3);
        state.camera.updateProjectionMatrix();
        state.controls.target.copy(state.camera.userData.cameraTargetPositionV3);
        state.controls.update();
      }
    }
  }, RENDER_PRIORITY_CAMERA_RESET);

  const environmentProps = lights.envMap.enabled ? getEnvironmentProps(lights.envMap) : undefined;
  const vFovDeg = camera.fov;
  const {maxDistance, minDistance} = CalcOrbitControlsDistance(vFovDeg);
  useGLSettings({ exposure: lights.exposure });
  const [modelReady, setModelReady] = useState(false);
  const { gl, scene:threeScene, camera:threeCamera } = useThree();

  const handleModelReady = useCallback((gltf, boundsBox) => {
    if (boundsBox && !boundsBox.isEmpty()) {
      const modelHalfHeight = (boundsBox.min.y + boundsBox.max.y) * 0.5;
      const {cameraUserData, cameraPositionV3} = getCameraProps({
        refCamera,
        refModel,
        modelHalfHeight,
        vFovDeg,
        cameraHorizontalAngle: camera.horizontalAngle,
        cameraVerticalAngle: camera.verticalAngle,
        cameraZoom: camera.zoom,
      });
      if (refCamera.current) {
        refCamera.current.fov = vFovDeg;
        refCamera.current.position.copy(cameraPositionV3);
        refCamera.current.lookAt(cameraUserData.cameraTargetPositionV3);
        refCamera.current.updateProjectionMatrix();
        cameraUserData.configChanged = true; // Will update position in useFrame
        refCamera.current.userData = cameraUserData;
      }
    }
    setModelReady(true);
    onModelReady(gltf);
  }, [onModelReady, refCamera, setModelReady, vFovDeg, camera.verticalAngle, camera.horizontalAngle, camera.zoom]);

  useEffect(() => {
    // Only set fake data when not always rendering
    if (noRender) {
      const subjectIsReady = lights.showFlatBackground || modelReady;
      if (subjectIsReady && onRenderingReady){
        debugLog(`Calling onRenderingReady with fake_data`);
        onRenderingReady(gl, threeScene, threeCamera, "fake_data");
      }
    }
  }, [noRender, modelReady, onRenderingReady, lights]);

  useEffect(() => {
    if (refCamera.current?.userData?.cameraTargetPositionV3) {
      const modelHalfHeight = refCamera.current.userData.cameraTargetPositionV3.y;
      const {cameraUserData} = getCameraProps({
        refCamera,
        refModel,
        modelHalfHeight,
        vFovDeg,
        cameraHorizontalAngle: camera.horizontalAngle,
        cameraVerticalAngle: camera.verticalAngle,
        cameraZoom: camera.zoom,
      });

      cameraUserData.configChanged = true; // Will update position in useFrame
      refCamera.current.userData = cameraUserData;
    }
  }, [refCamera, refModel, vFovDeg, camera.verticalAngle, camera.horizontalAngle, camera.zoom]);

  return (
    <>
      {debug.showHelpers && <Helpers />}
      <PerspectiveCamera makeDefault ref={refCamera}>
        {debug.orbitControls && <OrbitControls makeDefault minDistance={minDistance} maxDistance={maxDistance} />}
      </PerspectiveCamera>
      <DreiStage
        shadows={shadows}
        intensity={lights.intensity}
        preset={'soft'}
        reflectiveFloor={scene.reflectiveFloor}
        sceneConfig={scene}
        hdriYRotation={environmentProps.y}
        useHdriLightingOnly={lights.useHdriLightingOnly}
      />
      <group name="model" ref={refModel} visible={!debug?.invisibleModelParent}>
      {model?.url && 
        <Model
          key={model.url}
          url={model.url}
          rotate={model.rotate}
          // We ignore dontScale so we'll show the preview
          // in correct scale and position in QR mode.
          // dontScale={model.dontScale}
          showHelper={debug.modelHelper}
          showWireframe={debug.showWireframe}
          onLoadProgress={onModelLoadProgress}
          showModelStats={debug.showModelStats}
          onModelReady={handleModelReady}
          onClick={onModelClick}
          onPointerOver={onModelPointerOver}
          onPointerOut={onModelPointerOut}
          modelContent={modelContent}
          debugPrintStructure={debug.printStructure}
          debugPrintMeshDetailsOnClick={debug.printMeshDetailsOnClick}
        />
      }
	      {children}
      </group>
      {
          textNodes?.enabled && textNodes.nodes && <group name="text_nodes">
            {textNodes.nodes.map((textNode, index) => {
              return (
                <TextNode key={index} {...textNode} />
              )
            })}
          </group>
        }

      {lights.showFlatBackground ?
        <HDRIFlatBackground hdriURL={lights.envMap.preset} /> :
            environmentProps &&
                <CustomEnvironment
                    intensity={lights.intensity * 2}
                    files={environmentProps.files}
                    lockHdriRotationToCamera={lights.lockHdriRotationToCamera}
                    {...environmentProps}
        />
      }
      <PostProcessing {...postProcessing} mode={general.toneMapping}/>
    </>
  );
};
